export const IMAGE_CDN_ORIGIN = (
  process.env.NEXT_PUBLIC_IMAGE_CDN_ORIGIN || ''
).replace(/\/$/, '');

export const IMAGE_CDN_HOSTNAME = (() => {
  try {
    return IMAGE_CDN_ORIGIN ? new URL(IMAGE_CDN_ORIGIN).hostname : '';
  } catch {
    return '';
  }
})();

let isImageOptimisationEnabled = false;

function isImgixHostname(hostname: string): boolean {
  return hostname.endsWith('.imgix.net');
}

export function setImageOptimisationEnabled(enabled: boolean): void {
  isImageOptimisationEnabled = enabled;
}

export function getImageOptimisationEnabled(): boolean {
  return isImageOptimisationEnabled;
}

export function isImgixUrl(url: string): boolean {
  try {
    return isImgixHostname(new URL(url).hostname);
  } catch {
    return false;
  }
}

export function rewriteImgixToImageCdn(
  url: string,
  enabled = getImageOptimisationEnabled()
): string {
  if (!enabled || !IMAGE_CDN_ORIGIN) {
    return url;
  }

  if (!url || (!url.startsWith('https://') && !url.startsWith('http://'))) {
    return url;
  }

  try {
    const parsed = new URL(url);
    if (!isImgixHostname(parsed.hostname)) {
      return url;
    }

    const cdnBase = new URL(`${IMAGE_CDN_ORIGIN}/`);
    parsed.protocol = cdnBase.protocol;
    parsed.hostname = cdnBase.hostname;
    parsed.port = cdnBase.port;

    return parsed.href;
  } catch {
    return url;
  }
}

type ImageCdnUrlOptions = {
  width?: number;
  quality?: number;
  fit?: string;
  format?: string;
  auto?: string;
  enabled?: boolean;
};

export function buildImageCdnUrl(
  url: string,
  options: ImageCdnUrlOptions = {}
): string {
  const isEnabled = options.enabled ?? getImageOptimisationEnabled();
  const rewritten = rewriteImgixToImageCdn(url, isEnabled);

  if (
    !rewritten ||
    (!rewritten.startsWith('https://') && !rewritten.startsWith('http://'))
  ) {
    return rewritten;
  }

  try {
    const parsed = new URL(rewritten);

    const params = parsed.searchParams;

    if (options.format) {
      params.delete('auto');
      params.set('format', options.format);
    } else if (options.auto) {
      params.delete('format');
      params.set('auto', options.auto);
    }

    if (options.fit) {
      params.set('fit', options.fit);
    }

    if (options.width) {
      params.set('w', options.width.toString());
    }

    if (options.quality) {
      params.set('q', options.quality.toString());
    }

    return parsed.href;
  } catch {
    return rewritten;
  }
}
